import React from 'react';
import { pickCustomFieldProps, getFieldValue } from '../../util/fieldHelpers';
import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT } from '../../util/types';

import css from './ProfilePage.module.css';

// Helper to format URL for display
const formatUrlForDisplay = (url) => {
  if (!url) return '';
  return url.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
};

// Helper to ensure URL has protocol
const ensureProtocol = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

// Render a clickable URL field
const UrlField = ({ value, label }) => {
  if (!value) return null;

  // Handle multiple URLs (comma or newline separated)
  const urls = value.split(/[,\n]/).map(u => u.trim()).filter(Boolean);

  return (
    <div className={css.vendorFieldRow}>
      <span className={css.vendorFieldLabel}>{label}</span>
      <div className={css.vendorFieldValue}>
        {urls.map((url, idx) => (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>|</span>}
            <a
              href={ensureProtocol(url)}
              target="_blank"
              rel="noopener noreferrer"
              className={css.vendorLink}
            >
              {formatUrlForDisplay(url)}
              <span className={css.vendorLinkIcon}>&#8599;</span>
            </a>
          </span>
        ))}
      </div>
    </div>
  );
};

// Render multi-enum as a clean list
const MultiEnumList = ({ options, selectedOptions, label }) => {
  if (!selectedOptions || selectedOptions.length === 0) return null;

  const selectedSet = new Set(selectedOptions);
  const selectedItems = options?.filter(opt => selectedSet.has(opt.key)) || [];

  return (
    <div className={css.vendorFieldRowStacked}>
      <span className={css.vendorFieldLabel}>{label}</span>
      <ul className={css.vendorList}>
        {selectedItems.map(item => (
          <li key={item.key} className={css.vendorListItem}>
            <span className={css.vendorListBullet}>&#10003;</span>
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Render a simple text field
const TextField = ({ value, label }) => {
  if (!value) return null;

  return (
    <div className={css.vendorFieldRow}>
      <span className={css.vendorFieldLabel}>{label}</span>
      <span className={css.vendorFieldValue}>{value}</span>
    </div>
  );
};

// Section component with icon and title
const Section = ({ icon, iconClass, title, children }) => {
  // Check if there's any content to render
  const hasContent = React.Children.toArray(children).some(child => child !== null);
  if (!hasContent) return null;

  return (
    <div className={css.vendorSection}>
      <div className={css.vendorSectionHeader}>
        <div className={iconClass}>
          {icon}
        </div>
        <h3 className={css.vendorSectionTitle}>{title}</h3>
      </div>
      <div className={css.vendorSectionContent}>
        {children}
      </div>
    </div>
  );
};

// Field key patterns to identify field types
// These patterns match common naming conventions from Sharetribe Console
const FIELD_PATTERNS = {
  servicesOffered: /services.*offered|servicesoffered/i,
  propertyTypes: /property.*type|propertytype/i,
  yearsInBusiness: /years.*business|yearsinbusiness|experience/i,
  maxTravelDistance: /travel.*distance|traveldistance|service.*radius|serviceradius|maximum.*travel/i,
  countiesServed: /counties.*served|countiesserved|service.*area|counties/i,
  websiteUrl: /website.*url|websiteurl|website/i,
  socialMediaUrl: /social.*media|socialmedia|social.*url/i,
  contractorLicense: /contractor.*license|license.*number|licensenumber/i,
  certifications: /certification|specialt/i,
  // Hidden contact fields - we categorize these but don't display them
  phoneNumber: /phone|mobile|cell|telephone/i,
  emailAddress: /email|e-mail/i,
  address: /address|street|city|state|zip|postal|location/i,
};

// Match a field by its key against known patterns
const matchFieldPattern = (key, pattern) => pattern.test(key);

const VendorProfileSection = props => {
  const { publicData, metadata, userFieldConfig, intl } = props;

  if (!publicData || !userFieldConfig) {
    return null;
  }

  // Check if this is a provider user type
  const userType = publicData?.userType;
  const isProvider = userType === 'provider' || userType === 'vendor';

  if (!isProvider) {
    return null;
  }

  // Get all custom field props using the same helper as the default profile
  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      userFieldConfig,
      'userType',
      shouldPickUserField
    ) || [];

  // Also get enum, boolean, and long fields that aren't captured by pickCustomFieldProps
  const detailFields = [];
  userFieldConfig?.forEach(config => {
    const { key, schemaType, enumOptions, userTypeConfig = {}, showConfig = {} } = config;
    const { limitToUserTypeIds, userTypeIds } = userTypeConfig;
    const isTargetUserType = !limitToUserTypeIds || userTypeIds?.includes(userType);
    const { label, displayInProfile } = showConfig;

    if (!displayInProfile || !isTargetUserType) return;

    const publicDataValue = getFieldValue(publicData, key);
    const metadataValue = getFieldValue(metadata, key);
    const value = publicDataValue !== null ? publicDataValue : metadataValue;

    if (value !== null && (schemaType === 'enum' || schemaType === 'boolean' || schemaType === 'long')) {
      const findSelectedOption = enumValue => enumOptions?.find(o => enumValue === `${o.option}`);
      const optionConfig = findSelectedOption(value);

      detailFields.push({
        key,
        schemaType,
        label,
        value,
        displayValue: schemaType === 'enum' ? optionConfig?.label : value,
        options: enumOptions,
      });
    }
  });

  // Categorize fields by their type/purpose
  const categorizedFields = {
    servicesOffered: null,
    propertyTypes: null,
    yearsInBusiness: null,
    maxTravelDistance: null,
    countiesServed: null,
    websiteUrl: null,
    socialMediaUrl: null,
    contractorLicense: null,
    certifications: null,
    // Hidden contact fields - categorized but not displayed
    phoneNumber: null,
    emailAddress: null,
    address: null,
    other: [],
  };

  // Categorize multi-enum and text fields
  propsForCustomFields.forEach(field => {
    const key = field.key?.toLowerCase() || '';
    let matched = false;

    for (const [category, pattern] of Object.entries(FIELD_PATTERNS)) {
      if (matchFieldPattern(key, pattern)) {
        categorizedFields[category] = field;
        matched = true;
        break;
      }
    }

    if (!matched) {
      categorizedFields.other.push(field);
    }
  });

  // Categorize enum, boolean, and long fields
  detailFields.forEach(field => {
    const key = field.key?.toLowerCase() || '';
    let matched = false;

    for (const [category, pattern] of Object.entries(FIELD_PATTERNS)) {
      if (matchFieldPattern(key, pattern) && !categorizedFields[category]) {
        // Convert to a consistent format
        if (field.schemaType === 'long') {
          categorizedFields[category] = { ...field, text: String(field.value) };
        } else if (field.schemaType === 'enum') {
          categorizedFields[category] = {
            ...field,
            selectedOptions: [String(field.value)],
            options: field.options?.map(o => ({ key: String(o.option), label: o.label })) || [],
          };
        }
        matched = true;
        break;
      }
    }

    if (!matched) {
      categorizedFields.other.push({
        ...field,
        heading: field.label,
        text: field.displayValue,
        schemaType: SCHEMA_TYPE_TEXT,
      });
    }
  });

  // Extract categorized fields
  const servicesOffered = categorizedFields.servicesOffered;
  const propertyTypes = categorizedFields.propertyTypes;
  const yearsInBusiness = categorizedFields.yearsInBusiness;
  const maxTravelDistance = categorizedFields.maxTravelDistance;
  const countiesServed = categorizedFields.countiesServed;
  const websiteUrl = categorizedFields.websiteUrl;
  const socialMediaUrl = categorizedFields.socialMediaUrl;
  const contractorLicense = categorizedFields.contractorLicense;
  const certifications = categorizedFields.certifications;
  const otherFields = categorizedFields.other;

  // Check if we have any vendor fields to display
  const hasVendorFields = servicesOffered || propertyTypes || yearsInBusiness ||
    maxTravelDistance || countiesServed || websiteUrl || socialMediaUrl ||
    contractorLicense || certifications || otherFields.length > 0;

  if (!hasVendorFields) {
    return null;
  }

  // Helper to get display value for enum fields
  const getEnumDisplayValue = (field) => {
    if (!field?.selectedOptions?.[0]) return null;
    const selected = field.selectedOptions[0];
    const option = field.options?.find(o => o.key === selected);
    return option?.label || selected;
  };

  return (
    <div className={css.vendorProfile}>
      {/* Quick Stats */}
      {(yearsInBusiness?.text || maxTravelDistance || countiesServed) && (
        <div className={css.vendorQuickStats}>
          {yearsInBusiness?.text && (
            <div className={css.quickStat}>
              <div className={css.quickStatValue}>{yearsInBusiness.text}+</div>
              <div className={css.quickStatLabel}>Years Experience</div>
            </div>
          )}
          {maxTravelDistance && getEnumDisplayValue(maxTravelDistance) && (
            <div className={css.quickStat}>
              <div className={css.quickStatValue}>
                {getEnumDisplayValue(maxTravelDistance)}
              </div>
              <div className={css.quickStatLabel}>Service Radius</div>
            </div>
          )}
        </div>
      )}

      <div className={css.vendorSectionsGrid}>
        {/* Services Section */}
        <Section
          icon="&#128295;"
          iconClass={css.vendorSectionIconServices}
          title="Services"
        >
          {servicesOffered && (
            <MultiEnumList
              options={servicesOffered.options}
              selectedOptions={servicesOffered.selectedOptions}
              label="Services Offered"
            />
          )}
          {propertyTypes && (
            <MultiEnumList
              options={propertyTypes.options}
              selectedOptions={propertyTypes.selectedOptions}
              label="Property Types"
            />
          )}
        </Section>

        {/* Service Area Section */}
        <Section
          icon="&#128205;"
          iconClass={css.vendorSectionIconArea}
          title="Service Area"
        >
          {maxTravelDistance && getEnumDisplayValue(maxTravelDistance) && (
            <TextField
              value={getEnumDisplayValue(maxTravelDistance)}
              label="Max Travel Distance"
            />
          )}
        </Section>

        {/* Credentials Section */}
        <Section
          icon="&#127942;"
          iconClass={css.vendorSectionIconCredentials}
          title="Credentials"
        >
          {contractorLicense?.text && (
            <TextField value={contractorLicense.text} label="License #" />
          )}
          {certifications?.text && (
            <div className={css.vendorFieldRowStacked}>
              <span className={css.vendorFieldLabel}>Certifications & Specialties</span>
              <ul className={css.certificationsList}>
                {certifications.text.split(/[,\n]/).filter(Boolean).map((cert, idx) => (
                  <li key={idx} className={css.certificationItem}>
                    <span className={css.certificationIcon}>&#10003;</span>
                    <span className={css.certificationText}>{cert.trim()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {yearsInBusiness?.text && (
            <TextField value={`${yearsInBusiness.text} years`} label="Experience" />
          )}
        </Section>

        {/* Contact Section */}
        <Section
          icon="&#128279;"
          iconClass={css.vendorSectionIconContact}
          title="Connect"
        >
          {websiteUrl?.text && (
            <UrlField value={websiteUrl.text} label="Website" />
          )}
          {socialMediaUrl?.text && (
            <UrlField value={socialMediaUrl.text} label="Social Media" />
          )}
        </Section>

        {/* Other Fields (fallback for any fields not matching patterns) */}
        {otherFields.length > 0 && (
          <Section
            icon="&#128196;"
            iconClass={css.vendorSectionIconServices}
            title="Additional Info"
          >
            {otherFields.map(field => {
              if (field.schemaType === SCHEMA_TYPE_MULTI_ENUM) {
                return (
                  <MultiEnumList
                    key={field.key}
                    options={field.options}
                    selectedOptions={field.selectedOptions}
                    label={field.heading}
                  />
                );
              } else if (field.schemaType === SCHEMA_TYPE_TEXT) {
                return (
                  <TextField
                    key={field.key}
                    value={field.text}
                    label={field.heading}
                  />
                );
              }
              return null;
            })}
          </Section>
        )}
      </div>
    </div>
  );
};

export default VendorProfileSection;
